import { useEffect, useMemo, useState } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Icon,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { styled } from "@mui/material/styles";

import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import API_BASE_URL from "config/api";
import {
  buildConsentModeScript,
  fetchIntegrationReadiness,
} from "services/integrationReadiness";

const CodeBlock = styled("pre")(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  overflowX: "auto",
  margin: `${theme.spacing(1.5)} 0`,
  fontSize: "0.8125rem",
  lineHeight: 1.55,
  fontFamily: "monospace",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
}));

const SecondaryActionButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${theme.palette.grey[700]}`,
  color: theme.palette.grey[900],
  fontWeight: 600,
  padding: theme.spacing(1, 2),
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
    borderColor: theme.palette.grey[900],
  },
  "&.Mui-disabled": {
    backgroundColor: theme.palette.grey[100],
    borderColor: theme.palette.grey[300],
    color: theme.palette.grey[600],
  },
}));

const getSites = (me) =>
  (me?.user?.clients || []).flatMap((client) =>
    (client.sites || []).map((site) => ({
      ...site,
      tenantId: client.clientId,
    }))
  );

function GoogleConsentMode() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [defaultPreferencesAllowed, setDefaultPreferencesAllowed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadSites = async () => {
      try {
        const cached = sessionStorage.getItem("userMeData");
        let me = cached ? JSON.parse(cached) : null;
        if (!me) {
          const response = await fetch(`${API_BASE_URL}/me`, {
            headers: { Authorization: `Bearer ${sessionStorage.getItem("authToken")}` },
          });
          if (!response.ok) throw new Error("Site bilgileri alınamadı");
          me = await response.json();
          sessionStorage.setItem("userMeData", JSON.stringify(me));
        }
        if (cancelled) return;

        const availableSites = getSites(me);
        const requestedSiteId = new URLSearchParams(location.search).get("siteId");
        const requestedSiteExists = availableSites.some(
          (site) => String(site.siteId) === String(requestedSiteId)
        );
        setSites(availableSites);
        setSelectedSiteId(
          requestedSiteExists
            ? String(requestedSiteId)
            : String(availableSites[0]?.siteId || "")
        );
      } catch (loadError) {
        if (!cancelled) setError(loadError.message);
      }
    };
    loadSites();
    return () => {
      cancelled = true;
    };
  }, [location.search]);

  useEffect(() => {
    if (!selectedSiteId) {
      setReadiness(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    fetchIntegrationReadiness(
      selectedSiteId,
      sessionStorage.getItem("authToken"),
      controller.signal
    )
      .then((result) => {
        if (!cancelled) setReadiness(result);
      })
      .catch((readinessError) => {
        if (!cancelled && readinessError.name !== "AbortError") {
          setError(readinessError.message);
          setReadiness(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [selectedSiteId]);

  const selectedSite = useMemo(
    () => sites.find((site) => String(site.siteId) === String(selectedSiteId)),
    [selectedSiteId, sites]
  );
  const consentModeScript = buildConsentModeScript(selectedSite, defaultPreferencesAllowed);
  const canShowInstructions = Boolean(
    !loading &&
    readiness?.integration?.canShowGtm &&
    String(readiness?.site?.id || "") === String(selectedSiteId)
  );

  return (
    <DashboardLayout>
      <MDBox mt={2} mb={4}>
        <Grid container justifyContent="center">
          <Grid item xs={12} xl={10}>
            <Card sx={{ boxShadow: 3, overflow: "visible" }}>
              <Box p={{ xs: 2, md: 3 }}>
                <Box
                  display="flex"
                  flexDirection={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", md: "flex-start" }}
                  gap={2}
                >
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      Google Consent Mode v2
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                      Google etiketlerinin davranışını ziyaretçinin Logicookie tercihleriyle
                      eşleştirin.
                    </Typography>
                  </Box>
                  <Box sx={{ width: { xs: "100%", md: 320 }, flexShrink: 0 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                      Web sitesi
                    </Typography>
                    <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                      <Select
                        value={selectedSiteId}
                        onChange={(event) => setSelectedSiteId(String(event.target.value))}
                        IconComponent={KeyboardArrowDownIcon}
                        inputProps={{ "aria-label": "Web sitesi" }}
                        sx={{ minHeight: 44, bgcolor: "background.paper" }}
                      >
                        {sites.map((site) => (
                          <MenuItem key={site.siteId} value={String(site.siteId)}>
                            {site.domain}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {loading ? (
                  <Box py={7} display="flex" justifyContent="center">
                    <CircularProgress size={32} />
                  </Box>
                ) : error ? (
                  <Alert severity="error">{error}</Alert>
                ) : !canShowInstructions ? (
                  <Alert
                    severity="warning"
                    action={(
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => navigate(`/integrations/cmp-banner?siteId=${selectedSiteId}`)}
                      >
                        Entegrasyona git
                      </Button>
                    )}
                  >
                    Önce kurulum kodunu, banner ayarlarını, ilk taramayı ve başlangıç katalog
                    kontrolünü tamamlayın.
                  </Alert>
                ) : (
                  <Stack spacing={3}>
                    <Alert severity="success">
                      Logicookie loader ve başlangıç kataloğu doğrulandı. Aşağıdaki başlatma
                      kodu seçili siteye özel tenantId ve siteId değerlerini içerir.
                    </Alert>

                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        GTM Custom HTML etiketi
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mt={0.5}>
                        Bu kodu Google Tag Manager içinde <strong>Consent Initialization - All Pages</strong>
                        tetikleyicisiyle çalıştırın. Siteye eklenen loader kodunu burada tekrar eklemeyin.
                      </Typography>
                      <FormControlLabel
                        sx={{ mt: 1.5, mb: 0.5 }}
                        control={(
                          <Switch
                            checked={defaultPreferencesAllowed}
                            onChange={(event) => setDefaultPreferencesAllowed(event.target.checked)}
                            color="info"
                          />
                        )}
                        label={defaultPreferencesAllowed
                          ? "Çerez kategori varsayılan izinleri açık"
                          : "Çerez kategori varsayılan izinleri kapalı"}
                      />
                      <CodeBlock>{consentModeScript}</CodeBlock>
                      <CopyToClipboard
                        text={consentModeScript}
                        onCopy={() => {
                          setCopied(true);
                          window.setTimeout(() => setCopied(false), 2000);
                        }}
                      >
                        <SecondaryActionButton startIcon={<Icon>content_copy</Icon>}>
                          {copied ? "Kopyalandı" : "Kodu kopyala"}
                        </SecondaryActionButton>
                      </CopyToClipboard>
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="h6" fontWeight="bold" mb={2}>
                        Google Tag Manager ayarları
                      </Typography>
                      <Stack component="ol" spacing={2} sx={{ pl: 2.5, my: 0 }}>
                        <Typography component="li" variant="body2">
                          Yeni bir <strong>Custom HTML</strong> etiketi oluşturup yukarıdaki kodu
                          yapıştırın. Tetikleyici olarak <strong>Consent Initialization - All Pages</strong>
                          seçin.
                        </Typography>
                        <Typography component="li" variant="body2">
                          Başlatma seçeneklerine <strong>lang</strong> eklemeyin. Banner varsayılan
                          dili, panelde Dil Ayarları altında seçilen site ayarından alır.
                        </Typography>
                        <Typography component="li" variant="body2">
                          Google Tag etiketinizde yerleşik consent kontrollerini koruyun. Google
                          etiketine exception veya ek consent engeli eklemeyin.
                        </Typography>
                        <Typography component="li" variant="body2">
                          Preview ile siteyi açın. Tag Assistant’ın Consent sekmesinde ilk durumda
                          <strong> ad_storage</strong>, <strong>analytics_storage</strong>,
                          <strong> ad_user_data</strong> ve <strong>ad_personalization</strong>
                          ile <strong>functionality_storage</strong> değerlerinin
                          {" "}<strong>Denied</strong>; temel güvenlik işlevleri için
                          {" "}<strong>security_storage</strong> değerinin <strong>Granted</strong>
                          olduğunu doğrulayın.
                        </Typography>
                        <Typography component="li" variant="body2">
                          Banner’da analitik veya pazarlama izni verin. Aynı Consent sekmesinde
                          ilgili sinyallerin <strong>Granted</strong> olarak güncellendiğini test
                          ettikten sonra GTM container’ını yayınlayın.
                        </Typography>
                      </Stack>
                    </Box>

                    <Alert severity="info">
                      Bu kurulum gelişmiş Consent Mode davranışıdır: izin reddedildiğinde ilgili
                      Google ölçüm çerezleri yazılmaz, ancak Google etiketleri çerezsiz ölçüm
                      sinyalleri gönderebilir. Consent Mode tek başına hukuki uyum garantisi
                      değildir; hangi etiketlerin hangi hukuki dayanakla çalışacağı veri
                      sorumlusunun kararıdır.
                    </Alert>
                  </Stack>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default GoogleConsentMode;
